import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

export type Props = {
  readonly name?: string;
  readonly placeholder?: string;
  readonly error?: boolean | React.ReactNode;
  readonly errorPosition?: 'top' | 'bottom';
  readonly size?: 'small' | 'medium';
  readonly type?: 'text' | 'email' | 'password' | 'number';
  readonly value: string | number;
  readonly defaultValue?: string;
  readonly disabled?: boolean;
  readonly onChange: (value: string) => void;
  readonly onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  readonly onBlur?: (event: string) => void;
  readonly required?: boolean;
  readonly className?: string;
  readonly id?: string;
  readonly onKeyDown?: (event: KeyboardEvent) => void;
  readonly maxLength?: number;
  readonly testId?: string;
  readonly icon?: React.ReactNode;
  readonly min?: number;
  readonly max?: number;
  readonly stepSize?: number;
};

const StyledInputContainer = styled.div<{
  readonly error: Props['error'];
  readonly $size: Props['size'];
}>`
  position: relative;
  display: flex;
  border-radius: 4px;
  gap: 4px;
  padding: ${({ $size }) => ($size === 'small' ? 4 : 8)}px;
  font-size: ${({ $size }) => ($size === 'small' ? 12 : 16)}px;
  height: ${({ $size }) => ($size === 'small' ? 26 : 40)}px;
  border: 1px solid
    ${({ error, $size, theme }) =>
      theme.color(
        // eslint-disable-next-line no-nested-ternary
        error ? '#DB0000' : $size === 'small' ? '#8D8D8D' : '#B2B1BA',
      )};

  &:hover {
    opacity: 0.8;
  }

  &:focus-within {
    border: 1px solid #239ae7;
  }
`;

const StyledInput = styled.input<{ type?: string }>`
  /* Remove all styling except basics */
  border: none;
  width: ${({ type }) => (type === 'number' ? '4ch' : 'calc(100% - 24px)')};
  flex-grow: 1;
  background: transparent;
  font: inherit; /* inherit font-size etc from container */
  outline: none;
  ::placeholder {
    color: ${({ theme }) => theme.color('#B2B1BA')};
  }
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const StyledErrorMessage = styled.div<{
  errorPosition?: 'top' | 'bottom';
}>`
  color: #db0000;
  font-size: 12px;
  position: absolute;
  ${({ errorPosition }) =>
    errorPosition === 'top' ? 'bottom: 100%;' : 'top: 100%;'}
`;

export const Input = React.forwardRef<HTMLInputElement | null, Props>(
  (
    {
      name,
      className,
      placeholder,
      error,
      errorPosition = 'top',
      size = 'medium',
      required,
      value,
      defaultValue,
      disabled,
      onChange,
      onFocus,
      onBlur,
      type = 'text',
      onKeyDown,
      id,
      maxLength,
      testId,
      icon,
      min,
      max,
      stepSize = 1,
    },
    ref,
  ) => {
    const [internal, setInternal] = useState<string | number>(value);
    const validateNumber = (val: string | number) => {
      let validatedValue = Number(val);
      if (Number(validatedValue) < (min ?? 0)) {
        validatedValue = min ?? 0;
      } else if (Number(validatedValue) > (max ?? Infinity)) {
        validatedValue = max ?? Infinity;
      }
      setInternal(String(validatedValue));
      return String(validatedValue);
    };

    return (
      <div style={{ position: 'relative' }}>
        <StyledInputContainer
          error={
            error || (required && required && (value === null || value === ''))
          }
          $size={size}
          className={className}
        >
          {icon ?? null}
          <StyledInput
            id={id}
            name={name}
            onFocus={onFocus}
            step={stepSize}
            onBlur={(e) => {
              let eventValue = e.target.value;
              if (type === 'number') eventValue = validateNumber(eventValue);
              if (onBlur) onBlur(eventValue);
            }}
            disabled={Boolean(disabled)}
            onChange={(event) => {
              setInternal(event.target.value);
              onChange(event.target.value);
            }}
            placeholder={placeholder}
            onKeyDown={onKeyDown as any}
            ref={ref}
            required={required}
            type={type}
            defaultValue={defaultValue}
            value={type === 'number' ? internal : value}
            maxLength={maxLength}
            data-testid={testId}
            {...(type === 'number' ? { min } : {})}
            {...(type === 'number' ? { max } : {})}
          />
        </StyledInputContainer>
        {error && typeof error !== 'boolean' && (
          <StyledErrorMessage errorPosition={errorPosition}>
            {error}
          </StyledErrorMessage>
        )}
        {required && (value === null || value === '') && (
          <StyledErrorMessage errorPosition={errorPosition}>
            <FormattedMessage defaultMessage="Required" id="Seanpx" />
          </StyledErrorMessage>
        )}
      </div>
    );
  },
);

export default Input;
